import { useState } from 'react';
import { addToast, Table, Icon } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import {
  getStudentDocumentsTuitionDocumentDownload,
  getStudentDocumentsTuitionDocumentView,
} from '../../../api/studentDocumentsRequests';
import { useTexts } from '../hooks/useTexts';
import { StudentDocumentTuition } from '../types';
import { DocumentPreviewModal } from './DocumentPreviewModal';

type DocumentsTuitionTableProps = {
  studentId: number;
  data: StudentDocumentTuition[];
};

export function DocumentsTuitionTable({
  studentId,
  data,
}: DocumentsTuitionTableProps) {
  const texts = useTexts();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<{
    isOpen: boolean;
    url: string | null;
    title?: string;
  }>({ isOpen: false, url: null });
  const { t } = useTranslation();

  const getUrl = async (params: {
    type: string;
    documentId: number | string;
  }) => {
    setLoading(true);
    const result = await getStudentDocumentsTuitionDocumentDownload(
      studentId,
      params,
    );
    const { error, data } = result;
    setLoading(false);
    if (error && error.code === 'CONNECTION') {
      return addToast({
        icon: 'error',
        color: 'danger',
        text: 'error de conexión',
      });
    } else if (error) {
      return addToast({
        icon: 'error',
        color: 'danger',
        text: t('common.errors.get'),
      });
    }
    if (data) {
      window.open(data.url, '_blank');
    }
  };

  const viewDocument = async (params: {
    type: string;
    documentId: number | string;
    title: string;
  }) => {
    setLoading(true);
    const result = await getStudentDocumentsTuitionDocumentView(studentId, {
      type: params.type,
      documentId: params.documentId,
    });
    const { error, data } = result;
    setLoading(false);
    if (error && error.code === 'CONNECTION') {
      return addToast({
        icon: 'error',
        color: 'danger',
        text: 'error de conexión',
      });
    } else if (error) {
      return addToast({
        icon: 'error',
        color: 'danger',
        text: t('common.errors.get'),
      });
    }
    if (data) {
      setPreview({ isOpen: true, url: data.url, title: params.title });
    }
  };

  return (
    <>
      <Table
        data={data}
        noResultsText={texts.noResults}
        columns={[
          {
            headerText: texts.documentsTuitionTable.name,
            columnName: 'name',
            width: '25%',
            thClassName: 'text-center',
            tdClassName: 'text-center',
          },
          {
            headerText: texts.documentsTuitionTable.period,
            columnName: 'periodName',
            width: '25%',
            thClassName: 'text-center',
            tdClassName: 'text-center',
          },
          {
            headerText: texts.documentsTuitionTable.studyPlan,
            columnName: 'studyPlanName',
            width: '25%',
            thClassName: 'text-center',
            tdClassName: 'text-center',
          },
          {
            headerText: texts.documentsTuitionTable.fileName,
            columnName: 'filename',
            width: '25%',
            thClassName: 'text-center',
            tdClassName: 'text-center',
          },
          {
            headerText: texts.documentsTuitionTable.action,
            columnName: 'action',
            width: '25%',
            thClassName: 'text-center',
            tdClassName: 'text-center',
            cellFormat({ row }) {
              return (
                <>
                  <button
                    type="button"
                    className="btn"
                    onClick={() =>
                      viewDocument({
                        type: row.type,
                        documentId: row.documentId,
                        title: row.name,
                      })
                    }
                    disabled={loading}
                  >
                    <Icon name="eye" color="primary" />
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() =>
                      getUrl({ type: row.type, documentId: row.documentId })
                    }
                    disabled={loading}
                  >
                    <Icon name="download" color="primary" />
                  </button>
                </>
              );
            },
          },
        ]}
      />
      <DocumentPreviewModal
        isOpen={preview.isOpen}
        url={preview.url}
        title={preview.title}
        onClose={() => setPreview({ isOpen: false, url: null })}
      />
    </>
  );
}
